// src/pages/ManageLessons.jsx
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getCourse, getLessons, addLesson, deleteLesson, updateLesson } from "../firebase/database";

export default function ManageLessons() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [formData, setFormData] = useState({ title: "", content: "", category: "class" });
  const [error, setError] = useState("");

  useEffect(() => {
    loadData();
  }, [courseId]);

  const loadData = async () => {
    try {
      const [courseData, lessonsData] = await Promise.all([
        getCourse(courseId),
        getLessons(courseId)
      ]);
      setCourse(courseData);
      setLessons(lessonsData);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ title: "", content: "", category: "class" });
    setEditingLessonId(null);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.title.trim()) {
      setError("Lesson title is required");
      return;
    }

    setAdding(true);
    try {
      if (editingLessonId) {
        await updateLesson(courseId, editingLessonId, formData);
      } else {
        await addLesson(courseId, formData);
      }
      resetForm();
      loadData();
    } catch (err) {
      console.error("Error saving lesson:", err);
      setError(editingLessonId ? "Failed to update lesson" : "Failed to add lesson");
    } finally {
      setAdding(false);
    }
  };

  const handleEdit = (lesson) => {
    setFormData({
      title: lesson.title,
      content: lesson.content || "",
      category: lesson.category || "class"
    });
    setEditingLessonId(lesson.id);
    setError("");
  };

  const handleDelete = async (lessonId) => {
    if (!window.confirm("Are you sure you want to delete this lesson?")) {
      return;
    }
    try {
      await deleteLesson(courseId, lessonId);
      setLessons(lessons.filter(l => l.id !== lessonId));
    } catch (err) {
      console.error("Error deleting lesson:", err);
      alert("Failed to delete lesson");
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
        <div style={{ color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Loading...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>
        Course not found
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <Link 
            to="/admin/courses" 
            style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center" }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
            Manage Lessons
          </h1>
        </div>
        <p style={{ color: "#6B7280", margin: "4px 0 0 36px", fontFamily: "DM Sans, sans-serif" }}>
          {course.name} — {lessons.length} lessons
        </p>
      </div>

      {/* Add/Edit Lesson Form */}
      <div style={{ 
        background: "white", 
        borderRadius: 12, 
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)", 
        padding: 24, 
        marginBottom: 24 
      }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: "#1F2937", margin: "0 0 16px 0", fontFamily: "DM Sans, sans-serif" }}>
          {editingLessonId ? "Edit Lesson" : "Add Lesson"}
        </h2>
        
        {error && (
          <div style={{ 
            background: "#FEE2E2", 
            color: "#DC2626", 
            padding: "12px 16px", 
            borderRadius: 8, 
            marginBottom: 16,
            fontFamily: "DM Sans, sans-serif"
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: editingLessonId ? "1fr 150px 150px" : "1fr 150px", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ 
                display: "block", 
                fontSize: 14, 
                fontWeight: 600, 
                color: "#374151", 
                marginBottom: 8,
                fontFamily: "DM Sans, sans-serif"
              }}>
                Lesson Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Introduction to Python"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  fontSize: 16,
                  fontFamily: "DM Sans, sans-serif",
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 14, 
                fontWeight: 600, 
                color: "#374151", 
                marginBottom: 8,
                fontFamily: "DM Sans, sans-serif"
              }}>
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  fontSize: 16,
                  fontFamily: "DM Sans, sans-serif",
                  boxSizing: "border-box",
                  outline: "none",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
              >
                <option value="class">Class</option>
                <option value="practice">Practice</option>
              </select>
            </div>

            {editingLessonId && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: "12px 16px",
                    background: "white",
                    color: "#6B7280",
                    border: "1px solid #D1D5DB",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: "DM Sans, sans-serif",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ 
              display: "block", 
              fontSize: 14, 
              fontWeight: 600, 
              color: "#374151", 
              marginBottom: 8,
              fontFamily: "DM Sans, sans-serif"
            }}>
              Lesson Content (HTML Supported)
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Enter lesson content, notes, or instructions. You can use HTML tags like <b>, <i>, <h1>, <p>, <ul>, <li> etc."
              rows={12}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #D1D5DB",
                borderRadius: 8,
                fontSize: 16,
                fontFamily: "monospace",
                boxSizing: "border-box",
                outline: "none",
                resize: "vertical",
                lineHeight: "1.5",
              }}
            />
          </div>

          <button
            type="submit"
            disabled={adding}
            style={{
              padding: "12px 24px",
              background: adding ? "#9CA3AF" : "#3B82F6",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 600,
              fontFamily: "DM Sans, sans-serif",
              cursor: adding ? "not-allowed" : "pointer",
            }}
          >
            {adding ? (editingLessonId ? "Updating..." : "Adding...") : (editingLessonId ? "Update Lesson" : "Add Lesson")}
          </button>
        </form>
      </div>

      {/* Lessons List */}
      <div style={{ 
        background: "white", 
        borderRadius: 12, 
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)", 
        overflow: "hidden" 
      }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #E5E7EB" }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
            Lessons ({lessons.length})
          </h2>
        </div>

        {lessons.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>
            No lessons yet. Add your first lesson above.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {lessons.map((lesson, index) => (
              <div 
                key={lesson.id} 
                style={{ 
                  padding: "16px 24px", 
                  borderBottom: index < lessons.length - 1 ? "1px solid #E5E7EB" : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1 }}>
                  <span style={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: "50%", 
                    background: "#EEF2FF", 
                    color: "#4F46E5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "DM Sans, sans-serif"
                  }}>
                    {index + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: "#1F2937", fontFamily: "DM Sans, sans-serif" }}>
                      {lesson.title}
                    </div>
                    {lesson.content && (
                      <div style={{ fontSize: 14, color: "#6B7280", marginTop: 4, fontFamily: "DM Sans, sans-serif" }}>
                        {lesson.content.replace(/<[^>]*>?/gm, "").substring(0, 100)}{lesson.content.replace(/<[^>]*>?/gm, "").length > 100 ? "..." : ""}
                      </div>
                    )}
                  </div>
                </div>
                
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{
                    padding: "4px 12px",
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 500,
                    fontFamily: "DM Sans, sans-serif",
                    background: lesson.category === "class" ? "#DBEAFE" : "#D1FAE5",
                    color: lesson.category === "class" ? "#1D4ED8" : "#047857",
                  }}>
                    {lesson.category === "class" ? "Class" : "Practice"}
                  </span>
                  <button
                    onClick={() => handleEdit(lesson)}
                    style={{
                      padding: "6px 12px",
                      fontSize: 13,
                      background: "#E5E7EB",
                      color: "#374151",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontFamily: "DM Sans, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(lesson.id)}
                    style={{
                      padding: "6px 12px",
                      fontSize: 13,
                      background: "#FEE2E2",
                      color: "#DC2626",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontFamily: "DM Sans, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}